import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";

interface Endpoint {
  id: string;
  method: string;
  path: string;
  group: string;
  action: string;
}
interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
  permissions: string[];
}

const ApiKeysPage = () => {
  const { token } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [selected, setSelected] = useState<Endpoint[]>([]);
  const [name, setName] = useState("");
  const [expires, setExpires] = useState(() =>
    new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
  );
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoke, setRevoke] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };
  const docsUrl = `${(apiVariable || "").replace(/\/api\/?$/, "")}/swagger/index.html`;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [list, permissions] = await Promise.all([
        axios.get<ApiKey[]>(`${apiVariable}/api-keys`, config),
        axios.get<Endpoint[]>(`${apiVariable}/api-keys/endpoints`, config),
      ]);
      setKeys(list.data);
      setEndpoints(permissions.data);
    } catch {
      setError(
        "Could not load API keys. Check your access and that the API key database update has been applied.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError("");
    setCopied(false);
    try {
      const { data } = await axios.post<{ key: string; apiKey: ApiKey }>(
        `${apiVariable}/api-keys`,
        {
          name: name.trim(),
          expiresAt: new Date(`${expires}T23:59:59`).toISOString(),
          permissions: selected.map((e) => e.id),
        },
        { headers },
      );
      setSecret(data.key);
      setKeys((previous) => [data.apiKey, ...previous]);
      setName("");
      setSelected([]);
    } catch (e) {
      setError(
        axios.isAxiosError(e) && typeof e.response?.data === "string"
          ? e.response.data
          : "Could not create API key. Check the name, expiry, and selected endpoints.",
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revoke) return;
    setBusy(true);
    setError("");
    try {
      await axios.delete(`${apiVariable}/api-keys/${revoke.id}`, { headers });
      setRevoke(null);
      await load();
    } catch {
      setError("Could not revoke this key. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">API integrations</Typography>
        <Button href={docsUrl} target="_blank" rel="noopener noreferrer">
          Endpoint documentation
        </Button>
      </Stack>
      <Typography mb={2}>
        Create a separate key for each integration. Select the endpoints it can
        call and share the key securely with the provider.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Stack spacing={2} sx={{ mb: 4 }}>
        <TextField
          label="Integration name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 120 }}
        />
        <TextField
          type="date"
          label="Expires on"
          value={expires}
          onChange={(e) => setExpires(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Choose an expiry within the next year."
        />
        <Autocomplete
          multiple
          options={endpoints}
          value={selected}
          groupBy={(e) => e.group}
          getOptionLabel={(e) => `${e.method} ${e.path} · ${e.action}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, values) => setSelected(values)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Allowed endpoints"
              placeholder="Search by path or action"
            />
          )}
          renderTags={(values, getTagProps) =>
            values.map((value, index) => (
              <Chip label={value.id} {...getTagProps({ index })} size="small" />
            ))
          }
        />
        <Box>
          <Button
            variant="contained"
            disabled={
              busy ||
              loading ||
              !token ||
              !name.trim() ||
              !expires ||
              !selected.length
            }
            onClick={create}
          >
            {busy ? "Saving…" : "Create API key"}
          </Button>
        </Box>
      </Stack>
      <Typography variant="h6">Issued keys</Typography>
      {loading ? (
        <CircularProgress aria-label="Loading API keys" />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "Integration",
                  "Key prefix",
                  "Access",
                  "Expires",
                  "Status",
                  "",
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {key.prefix}…
                  </TableCell>
                  <TableCell>
                    <details>
                      <summary>{key.permissions.length} endpoints</summary>
                      {key.permissions.map((p) => (
                        <Typography
                          key={p}
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {p}
                        </Typography>
                      ))}
                    </details>
                  </TableCell>
                  <TableCell>
                    {new Date(key.expiresAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {key.revokedAt
                      ? "Revoked"
                      : new Date(key.expiresAt) <= new Date()
                        ? "Expired"
                        : "Active"}
                  </TableCell>
                  <TableCell>
                    <Button
                      color="error"
                      disabled={busy || !!key.revokedAt}
                      onClick={() => setRevoke(key)}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!keys.length && (
                <TableRow>
                  <TableCell colSpan={6}>No API keys created yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      <Dialog open={secret !== null} maxWidth="md" fullWidth>
        <DialogTitle>Copy your API key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time the full key will be shown. Save it securely
            before closing.
          </Alert>
          <TextField
            fullWidth
            multiline
            value={secret || ""}
            InputProps={{ readOnly: true }}
            label="API key"
          />
          <Typography mt={2}>
            Send it in the <code>X-API-Key</code> header over HTTPS.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(secret || "");
                setCopied(true);
              } catch {
                setError(
                  "Clipboard access failed. Select and copy the key from the field.",
                );
              }
            }}
          >
            {copied ? "Copied" : "Copy key"}
          </Button>
          <Button onClick={() => setSecret(null)}>I have saved the key</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!revoke}
        onClose={() => {
          if (!busy) setRevoke(null);
        }}
      >
        <DialogTitle>Revoke {revoke?.name}?</DialogTitle>
        <DialogContent>
          This integration will lose access immediately. Create a new key to
          restore its access.
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRevoke(null)}>
            Cancel
          </Button>
          <Button color="error" disabled={busy} onClick={confirmRevoke}>
            Revoke key
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeysPage;
